import { faBirthdayCake, faEllipsis, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExportTnACalendarToExcel from "./ExportTnACalendarToExcel/ExportTnACalendarToExcel";
import { AbsencesContainer, EmployeeNameContainer, MainTableContainer, ScheduleCode } from "./styles";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { RefModel } from "../../../models/base/ref-model";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { EmployeeDTO, EmployeeRef } from "../../../models/employees";
import { TnAAbsenceDTO } from "../../../models/tna";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { TnAScheduleIntervalRef } from "../../../models/tna/tna-schedule-intervals/tna-schedule-interval-ref";
import { TnACalendarDaysDTO } from "../../../models/tna-calendar-days/tna-calendar-days-dto";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../../reusable-components/Table/Table";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { TnAOverviewsCalendarsApiService } from "../../../services/tna-overviews-calendars/tna-overviews-calendars-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getDayAndDateAndMonth, getDaysInMonth, intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import AbsenceButton from "../../MyCalendar/AddEditAbsence/AbsenceButton";
import EditAbsenceWrapper from "../../MyCalendar/AddEditAbsence/EditAbsenceWrapper/EditAbsenceWrapper";
import { AbsenceStatusIndicator } from "../../MyCalendar/AddEditAbsence/styles";
import { ScheduleCodeWrapper } from "../../MyCalendar/DetailedFullCalendar/styles";
import ScheduleDetailsModal from "../../MyCalendar/ScheduleDetailsModal/ScheduleDetailsModal";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { getIntervalsDuration } from "../../SystemConfigurationTables/TnAConfig/TnASchedulesTable/TnAScheduleConfig/utils";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";

interface State {
  loading: boolean;
  error: string;
}

const TnACalendar = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_calendar";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const { forceUpdate } = useForceUpdate();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const allowedToApproveReject = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allItems = useRef<TnACalendarDaysDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  // #region context
  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);
  // #endregion

  // #region navigate
  const navigate = useNavigate();

  const clickNavigate = (employee: EmployeeDTO | EmployeeRef) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };
  // #endregion

  // #region columns
  const columns: GridColDef<TnACalendarDaysDTO>[] = [
    {
      field: "employee.employeeCode",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    row.employee && clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => <Typography>{`${t("common_words.tna_employee_group")}`}</Typography>,
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
  ];
  // #endregion

  // #region dynamic columns
  const dynamicColumns = (year: number, month: number) => {
    const result: GridColDef<TnACalendarDaysDTO>[] = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find(
            (day: TnADayDataRef) =>
              day?.tnADate?.day === i + 1 && day?.tnADate?.month === month + 1 && day?.tnADate?.year === year
          );
          const weekday = CustomDate.ToDate(currDay?.tnADate as CustomDate)?.getDay();

          const dayAnomalies =
            currDay?.anomalies?.filter((_anomaly) => !_anomaly.anomalyType?.isAbnormalPresence) ?? [];

          const hasAnomaly = currDay?.hasAnomaly || dayAnomalies.length > 0;
          const anomalyDescription =
            dayAnomalies.map((_dayAnomaly) => _dayAnomaly.anomalyType?.description).join(",\n") ??
            currDay?.anomalyDescription;

          return currDay?.absences.length ? (
            <AbsencesContainer>
              {currDay?.absences.map((absence: TnAAbsenceDTO, idx: number) => {
                if (idx > 0) return;
                return (
                  <AbsenceStatusIndicator
                    isRejected={absence.isRejected}
                    isCancelled={absence.isCancelled}
                    isApproved={absence.isApproved}
                    key={absence.id}
                  >
                    <EditAbsenceWrapper
                      absence={absence}
                      employee={row.employee}
                      handleNotifyParent={forceUpdate}
                      allowedToApproveReject={allowedToApproveReject}
                      viewMode={allowedToViewOnly && !hasAccess}
                      year={year}
                      month={month}
                      forManager={hasAccess}
                      allowedOnlyManagersAction
                    >
                      <AbsenceButton
                        absence={absence}
                        wrapContent
                      />
                    </EditAbsenceWrapper>
                  </AbsenceStatusIndicator>
                );
              })}

              {currDay?.absences.length > 1 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                  flow="column"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  customButtonStyle={{ minWidth: "20px", height: "20px" }}
                >
                  {currDay?.absences.slice(1).map((abs: TnAAbsenceDTO, idx: number) => {
                    return (
                      <div key={idx}>
                        <AbsenceStatusIndicator
                          isRejected={abs.isRejected}
                          isCancelled={abs.isCancelled}
                          isApproved={abs.isApproved}
                          key={abs.id}
                        >
                          <EditAbsenceWrapper
                            absence={abs}
                            employee={row.employee}
                            handleNotifyParent={forceUpdate}
                            allowedToApproveReject={allowedToApproveReject}
                            viewMode={allowedToViewOnly && !hasAccess}
                            year={year}
                            month={month}
                            forManager={hasAccess}
                            allowedOnlyManagersAction
                          >
                            <AbsenceButton
                              absence={abs}
                              wrapContent
                            />
                          </EditAbsenceWrapper>
                        </AbsenceStatusIndicator>
                      </div>
                    );
                  })}
                </SimplePopover>
              )}
            </AbsencesContainer>
          ) : (
            <React.Fragment>
              <React.Fragment>
                {hasAnomaly && (
                  <FontAwesomeIcon
                    className="fa-icon"
                    icon={faExclamationCircle}
                    style={{
                      marginLeft: 3,
                      opacity: 0.5,
                      color: theme.palette.error.main,
                    }}
                    title={`Anomaly: ${anomalyDescription}`}
                  />
                )}
              </React.Fragment>

              <React.Fragment>
                <Tooltip
                  title={currDay?.phNames as string}
                  placement="top"
                >
                  {currDay?.schedule ? (
                    <ScheduleCodeWrapper>
                      <ScheduleDetailsModal
                        intervals={currDay.schedule?.intervals as TnAScheduleIntervalRef[]}
                        name={currDay.schedule?.name as string}
                        description={currDay.schedule?.description as string}
                        code={currDay.schedule?.code as string}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <ScheduleCode
                            title={`${currDay.schedule?.name}`}
                            variant="body2"
                          >{`${currDay.schedule?.code?.trim() ? currDay.schedule?.code : "<?>"}`}</ScheduleCode>
                          <span
                            title={`${t(`${tRP}.expected_presence`)}`}
                            style={{ fontSize: "10px" }}
                          >
                            (
                            {currDay.schedule
                              ? `${(
                                  getIntervalsDuration(
                                    currDay.schedule.intervals.filter((interval) => interval.weekDay === weekday)
                                  ) /
                                  (1000 * 60 * 60)
                                ).toFixed(2)} h`
                              : ""}
                            )
                          </span>
                        </div>
                      </ScheduleDetailsModal>
                    </ScheduleCodeWrapper>
                  ) : (
                    <Typography sx={{ fontSize: "0.75rem", width: "max-content !important" }}>
                      {currDay?.phNames as string}
                    </Typography>
                  )}
                </Tooltip>
              </React.Fragment>
              {/* ))} */}
            </React.Fragment>
          );
        },
        headerName: getDayAndDateAndMonth(new Date(year, month, i + 1)),
        headerAlign: "center",
        align: "center",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataRef) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return thisDayData?.isPublicHoliday ? "isPublicHoliday" : isWeekend ? "isWeekend" : "";
        },
      });
    }

    return result;
  };
  // #endregion

  // #region default item
  const defaultItem: Partial<TnACalendarDaysDTO> = {
    employee: null,
    daysData: [],
  };
  // #endregion

  // #region load employee group
  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);
  // #endregion

  // #region load tna calendars data
  const loadTnACalendarsData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new TnAOverviewsCalendarsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );
  // #endregion

  // #region quickfilters
  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearMonthQuickFilter && yearMonthQuickFilter}
            <ExportTnACalendarToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnACalendarsData={loadTnACalendarsData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );
  // #endregion

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      <MainTableContainer>
        {(hasAccess || allowedToViewOnly) && (
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnAOverviewsCalendarsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year,
                month: month + 1,
              } as TnAOverviewsFilter
            }
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            readOnly
            quickFilters={quickfilters}
            dynamicColumns={dynamicColumns(year, month)}
            getAllItems={(items) => {
              allItems.current = items as TnACalendarDaysDTO[];
            }}
            pinnedColumns={{
              left: ["employee.employeeCode", "employee.name"],
            }}
          />
        )}
      </MainTableContainer>
    </LoadingOverlay>
  );
};

export default TnACalendar;
